import {
  forwardRef,
  type HTMLAttributes,
} from 'react'
import {
  cva,
  type VariantProps,
} from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const cardVariants = cva(
  [
    'min-w-0 max-w-full rounded-xl border',
    'border-(--color-border)',
    'text-(--color-foreground)',
    'transition-[border-color,box-shadow]',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-(--color-surface-raised)',
          'shadow-[var(--shadow-card)]',
        ],

        muted: [
          'bg-(--color-surface-muted)',
        ],

        transparent: [
          'border-transparent',
          'bg-transparent',
        ],
      },

      padding: {
        none: '',
        small: 'p-4',
        medium: 'p-6',
        large: 'p-8',
      },
    },

    defaultVariants: {
      variant: 'default',
      padding: 'none',
    },
  },
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card =
  forwardRef<
    HTMLDivElement,
    CardProps
  >(function Card(
    {
      variant,
      padding,
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({
            variant,
            padding,
          }),
          className,
        )}
        {...props}
      />
    )
  })

export const CardHeader =
  forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLDivElement>
  >(function CardHeader(
    {
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          [
            'flex items-start justify-between',
            'gap-4 border-b',
            'border-(--color-border)',
            'px-5 py-4 sm:px-6',
          ],
          className,
        )}
        {...props}
      />
    )
  })

export const CardTitle =
  forwardRef<
    HTMLHeadingElement,
    HTMLAttributes<HTMLHeadingElement>
  >(function CardTitle(
    {
      className,
      ...props
    },
    ref,
  ) {
    return (
      <h2
        ref={ref}
        className={cn(
          'text-[0.9375rem] font-semibold tracking-[-0.01em]',
          className,
        )}
        {...props}
      />
    )
  })

export const CardDescription =
  forwardRef<
    HTMLParagraphElement,
    HTMLAttributes<HTMLParagraphElement>
  >(function CardDescription(
    {
      className,
      ...props
    },
    ref,
  ) {
    return (
      <p
        ref={ref}
        className={cn(
          [
            'mt-1 text-sm leading-6',
            'text-(--color-muted-foreground)',
          ],
          className,
        )}
        {...props}
      />
    )
  })

export const CardContent =
  forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLDivElement>
  >(function CardContent(
    {
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          'px-5 py-5 sm:px-6',
          className,
        )}
        {...props}
      />
    )
  })

export const CardFooter =
  forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLDivElement>
  >(function CardFooter(
    {
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          [
            'flex items-center justify-end',
            'gap-3 border-t',
            'border-(--color-border)',
            'px-5 py-4 sm:px-6',
          ],
          className,
        )}
        {...props}
      />
    )
  })
